import React, { useState } from "react";
import {
  Box,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  CircularProgress,
} from "@mui/material";
import {
  Print as PrintIcon,
  Share as ShareIcon,
  PictureAsPdf as PdfIcon,
} from "@mui/icons-material";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { useOrganizationDataByOrgId } from "../../../../../hooks/useOrganizationData";
import { useRecoilValue } from "recoil";
import organizationIdState from "../../../../../atoms/organizationIdAtom";
import { usePDFGenerator } from "../../../../../hooks/usePDFGenerator";
import useRecipes from "../../../../../hooks/useRecipes";
import NewColors from "../../../../../themes/NewColors";

/**
 * RecipeFloatingActions component provides floating action buttons for recipe operations
 *
 * @param {Object} recipe - The recipe object
 */
const RecipeFloatingActions = ({ recipe }) => {
  const organizationId = useRecoilValue(organizationIdState);
  const { organizationData, loading } = useOrganizationDataByOrgId({
    organizationId,
  });
  const { editRecipe } = useRecipes({ organizationId });
  const [printMenuAnchor, setPrintMenuAnchor] = useState(null);
  const { generateRecipePDF, isGenerating } = usePDFGenerator();

  console.log("RecipeFloatingActions - organizationData:", organizationData);
  console.log("RecipeFloatingActions - recipe:", recipe);

  // If loading organization data, return null to avoid rendering before data is ready
  if (loading) return null;

  // Get branding colors from organization or use defaults
  const primaryColor = organizationData?.brandingColors?.primary || NewColors.active.primary;

  // Handle print recipe - use PDF generation for better quality
  const handlePrint = async () => {
    try {
      await generateRecipePDF(recipe, organizationData);
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Failed to generate PDF. Please try again.");
    }
  };

  // Handle PDF download - same as print for consistency
  const handleDownloadPDF = async () => {
    try {
      await generateRecipePDF(recipe, organizationData);
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Failed to generate PDF. Please try again.");
    }
  };

  // Handle share recipe with privacy check
  const handleShare = async () => {
    if (recipe.isPublic) {
      // If already public, open the public URL
      try {
        const baseUrl = window.location.origin;
        const publicRecipeUrl = `${baseUrl}/recipe/${organizationId}/${recipe.id}`;

        window.open(publicRecipeUrl, "_blank");

        const notification = document.createElement("div");
        notification.textContent = "Public recipe opened in new tab!";
        notification.style.cssText = `
          position: fixed;
          top: 20px;
          right: 20px;
          background: ${primaryColor};
          color: white;
          padding: 12px 20px;
          border-radius: 4px;
          z-index: 9999;
          font-family: Arial, sans-serif;
          box-shadow: 0 2px 8px rgba(0,0,0,0.2);
        `;
        document.body.appendChild(notification);

        setTimeout(() => {
          if (document.body.contains(notification)) {
            document.body.removeChild(notification);
          }
        }, 3000);
      } catch (error) {
        console.error("Error opening public recipe:", error);
      }
    } else {
      // If not public, ask if they want to make it public
      confirmAlert({
        title: "Make Recipe Public",
        message:
          "This recipe is currently private. Would you like to make it public so it can be shared?",
        buttons: [
          {
            label: "Yes, Make Public",
            onClick: async () => {
              try {
                // Update the recipe to be public
                await editRecipe({ ...recipe, isPublic: true });

                // Open the public URL after making it public
                const baseUrl = window.location.origin;
                const publicRecipeUrl = `${baseUrl}/recipe/${organizationId}/${recipe.id}`;

                setTimeout(() => {
                  window.open(publicRecipeUrl, "_blank");

                  const notification = document.createElement("div");
                  notification.textContent =
                    "Recipe made public and opened in new tab!";
                  notification.style.cssText = `
                    position: fixed;
                    top: 20px;
                    right: 20px;
                    background: ${primaryColor};
                    color: white;
                    padding: 12px 20px;
                    border-radius: 4px;
                    z-index: 9999;
                    font-family: Arial, sans-serif;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.2);
                  `;
                  document.body.appendChild(notification);

                  setTimeout(() => {
                    if (document.body.contains(notification)) {
                      document.body.removeChild(notification);
                    }
                  }, 3000);
                }, 500); // Small delay to allow the update to propagate
              } catch (error) {
                console.error("Failed to make recipe public:", error);
                alert("Failed to make recipe public. Please try again.");
              }
            },
          },
          {
            label: "Cancel",
            onClick: () => {},
          },
        ],
      });
    }
  };

  return (
    <Box
      sx={{
        position: "fixed",
        bottom: 20,
        right: 20,
        display: "flex",
        flexDirection: "column",
        gap: 1,
        zIndex: 10,
      }}
    >
      <Tooltip title="Print Options">
        <IconButton
          color="primary"
          size="large"
          sx={{
            bgcolor: "primary.main",
            color: "white",
            "&:hover": { bgcolor: "primary.dark" },
            "@media print": { display: "none" },
          }}
          onClick={(e) => setPrintMenuAnchor(e.currentTarget)}
          disabled={isGenerating}
        >
          {isGenerating ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            <PrintIcon />
          )}
        </IconButton>
      </Tooltip>

      {/* Print Menu */}
      <Menu
        anchorEl={printMenuAnchor}
        open={Boolean(printMenuAnchor)}
        onClose={() => setPrintMenuAnchor(null)}
        anchorOrigin={{
          vertical: "center",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "center",
          horizontal: "right",
        }}
        PaperProps={{
          sx: { minWidth: 180 },
        }}
      >
        <MenuItem
          onClick={() => {
            handlePrint();
            setPrintMenuAnchor(null);
          }}
          disabled={isGenerating}
        >
          <ListItemIcon>
            {isGenerating ? (
              <CircularProgress size={20} />
            ) : (
              <PrintIcon fontSize="small" />
            )}
          </ListItemIcon>
          Print Recipe
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleDownloadPDF();
            setPrintMenuAnchor(null);
          }}
          disabled={isGenerating}
        >
          <ListItemIcon>
            {isGenerating ? (
              <CircularProgress size={20} />
            ) : (
              <PdfIcon fontSize="small" />
            )}
          </ListItemIcon>
          Download PDF
        </MenuItem>
      </Menu>

      <Tooltip title="Open Public Recipe">
        <IconButton
          color="secondary"
          size="large"
          sx={{
            bgcolor: "secondary.main",
            color: "white",
            "&:hover": { bgcolor: "secondary.dark" },
            "@media print": { display: "none" },
          }}
          onClick={handleShare}
        >
          <ShareIcon />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export default RecipeFloatingActions;
